import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import JobCard from '../components/JobCard';
import { API_URL, formatDate, getStatusBadgeClass, licenseOptions } from '../lib/utils';
import { toast } from 'sonner';
import {
  Briefcase,
  Bookmark,
  FileText,
  User,
  Settings,
  Loader2,
  Upload,
  MapPin,
  Phone,
  Calendar,
  Building2,
  ExternalLink,
  Award,
  GraduationCap,
  Trash2,
  FileCheck,
  Archive,
  MessageSquare,
  Sparkles,
  ArrowRight,
  CheckCircle2,
  AlertCircle,
  FolderOpen,
} from 'lucide-react';
import MessagesPanel from '../components/MessagesPanel';

import { JobSeekerCtx } from './JobSeekerContext';
import JobSeekerTabOverview from './JobSeekerTabOverview';
import JobSeekerTabApplications from './JobSeekerTabApplications';
import JobSeekerTabMessages from './JobSeekerTabMessages';
import JobSeekerTabSaved from './JobSeekerTabSaved';
import JobSeekerTabProfile from './JobSeekerTabProfile';
import JobSeekerTabAlerts from './JobSeekerTabAlerts';
import JobSeekerTabEmailPrefs from './JobSeekerTabEmailPrefs';
import GettingStarted from '../components/GettingStarted';

const JobSeekerDashboard = () => {
  const { user, updateProfile, uploadResume, logout } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'overview');

  useEffect(() => {
    const t = searchParams.get('tab');
    if (t) setActiveTab(t);
  }, [searchParams]);

  useEffect(() => {
    axios.get(`${API_URL}/resource-folders`)
      .then((r) => setFolderCount((r.data.folders || []).length))
      .catch(() => {});
  }, []);
  const [applications, setApplications] = useState([]);
  const [savedJobs, setSavedJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [folderCount, setFolderCount] = useState(0);
  const [profileData, setProfileData] = useState({
    name: user?.name || '',
    license_type: user?.license_type || '',
    phone: user?.phone || '',
    location: user?.location || '',
    bio: user?.bio || '',
    years_experience: user?.years_experience || '',
    specializations: user?.specializations?.join(', ') || '',
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingLicense, setUploadingLicense] = useState(false);
  const [uploadingDegree, setUploadingDegree] = useState(false);
  const [deletingApp, setDeletingApp] = useState(null);
  const [deletingAccount, setDeletingAccount] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteStep, setDeleteStep] = useState('confirm'); // 'confirm' or 'reason'
  const [deleteReason, setDeleteReason] = useState('');
  
  // AI Resume Parser states
  const [parsingResume, setParsingResume] = useState(false);
  const [parsedResume, setParsedResume] = useState(null);
  const [showParseResults, setShowParseResults] = useState(false);

  useEffect(() => {
    if (user) {
      setProfileData({
        name: user.name || '',
        license_type: user.license_type || '',
        phone: user.phone || '',
        location: user.location || '',
        bio: user.bio || '',
        years_experience: user.years_experience || '',
        specializations: user.specializations?.join(', ') || '',
      });
    }
    fetchData();
  }, [user]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [appsRes, savedRes] = await Promise.all([
        axios.get(`${API_URL}/applications`, {
          headers: {  },
        }),
        axios.get(`${API_URL}/saved-jobs`, {
          headers: {  },
        }),
      ]);
      setApplications(appsRes.data);
      setSavedJobs(savedRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const updateData = {
        ...profileData,
        years_experience: profileData.years_experience ? parseInt(profileData.years_experience) : null,
        specializations: profileData.specializations
          ? profileData.specializations.split(',').map(s => s.trim()).filter(Boolean)
          : [],
      };
      await updateProfile(updateData);
      toast.success('Profile updated successfully!');
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleResumeUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    setUploading(true);
    try {
      await uploadResume(file);
      toast.success('Resume uploaded successfully!');
    } catch (error) {
      toast.error('Failed to upload resume');
    } finally {
      setUploading(false);
    }
  };

  // AI Resume Parser
  const handleParseResume = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    setParsingResume(true);
    setParsedResume(null);
    
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API_URL}/users/resume/parse`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
      });
      
      if (response.data.parsed_data) {
        setParsedResume(response.data.parsed_data);
        setShowParseResults(true);
        toast.success('Resume parsed successfully!');
      } else {
        toast.error('Could not parse resume');
      }
    } catch (error) {
      console.error('Resume parse error:', error);
      toast.error(error.response?.data?.detail || 'Failed to parse resume');
    } finally {
      setParsingResume(false);
    }
  };

  const applyParsedDataToProfile = () => {
    if (!parsedResume) return;
    
    setProfileData(prev => ({
      ...prev,
      name: parsedResume.name || prev.name,
      phone: parsedResume.phone || prev.phone,
      location: parsedResume.location || prev.location,
      bio: parsedResume.summary || prev.bio,
      years_experience: parsedResume.years_experience || prev.years_experience,
      specializations: parsedResume.skills?.join(', ') || prev.specializations,
    }));
    
    toast.success('Profile updated with parsed resume data!');
    setShowParseResults(false);
  };

  const handleLicenseUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    setUploadingLicense(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(`${API_URL}/users/license`, formData, {
        headers: { 
          
          'Content-Type': 'multipart/form-data'
        },
      });
      toast.success('License uploaded successfully!');
      window.location.reload(); // Refresh to show new document
    } catch (error) {
      toast.error('Failed to upload license');
    } finally {
      setUploadingLicense(false);
    }
  };

  const handleDegreeUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    setUploadingDegree(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(`${API_URL}/users/degree`, formData, {
        headers: { 
          
          'Content-Type': 'multipart/form-data'
        },
      });
      toast.success('Degree uploaded successfully!');
      window.location.reload(); // Refresh to show new document
    } catch (error) {
      toast.error('Failed to upload degree');
    } finally {
      setUploadingDegree(false);
    }
  };

  const handleDeleteDocument = async (docType) => {
    try {
      await axios.delete(`${API_URL}/users/document/${docType}`, {
        headers: {  },
      });
      toast.success(`${docType.charAt(0).toUpperCase() + docType.slice(1)} deleted successfully!`);
      window.location.reload();
    } catch (error) {
      toast.error(`Failed to delete ${docType}`);
    }
  };

  const handleDeleteAccount = async () => {
    setDeletingAccount(true);
    try {
      await axios.delete(`${API_URL}/users/account`, {
        headers: {  },
        data: { reason: deleteReason }
      });
      toast.success('Your account has been deleted');
      logout();
      navigate('/');
    } catch (error) {
      toast.error('Failed to delete account. Please contact support.');
    } finally {
      setDeletingAccount(false);
      setShowDeleteConfirm(false);
      setDeleteStep('confirm');
      setDeleteReason('');
    }
  };

  const handleWithdrawApplication = async (applicationId) => {
    if (!window.confirm('Are you sure you want to withdraw this application? This cannot be undone.')) {
      return;
    }
    
    setDeletingApp(applicationId);
    try {
      await axios.delete(`${API_URL}/applications/${applicationId}`, {
        headers: {  },
      });
      toast.success('Application withdrawn successfully');
      setApplications(prev => prev.filter(app => app.application_id !== applicationId));
    } catch (error) {
      toast.error('Failed to withdraw application');
    } finally {
      setDeletingApp(null);
    }
  };

  const handleArchiveApplication = async (applicationId) => {
    setDeletingApp(applicationId);
    try {
      await axios.put(`${API_URL}/applications/${applicationId}/archive`, {}, {
        headers: {  },
      });
      toast.success('Application archived');
      setApplications(prev => prev.filter(app => app.application_id !== applicationId));
    } catch (error) {
      toast.error('Failed to archive application');
    } finally {
      setDeletingApp(null);
    }
  };

  const handleUnsaveJob = async (jobId) => {
    try {
      await axios.delete(`${API_URL}/saved-jobs/${jobId}`, {
        headers: {  },
      });
      setSavedJobs(prev => prev.filter(job => job.job_id !== jobId));
      toast.success('Job removed from saved');
    } catch (error) {
      toast.error('Failed to remove job');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 pt-20 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div>
            <h1 className="font-serif text-3xl font-bold text-gray-900">
              Welcome, {user?.name?.split(' ')[0]}!
            </h1>
            <p className="text-gray-600">Manage your job search and applications</p>
          </div>
          <Link to="/jobs">
            <Button className="bg-primary hover:bg-primary-hover text-white" data-testid="browse-jobs-btn">
              <Briefcase className="w-4 h-4 mr-2" />
              Browse All Jobs
            </Button>
          </Link>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-xl p-5 border border-gray-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <FileText className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{applications.length}</p>
                <p className="text-sm text-gray-500">Applications</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-5 border border-gray-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <Bookmark className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{savedJobs.length}</p>
                <p className="text-sm text-gray-500">Saved Jobs</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-5 border border-gray-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                <Calendar className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">
                  {applications.filter(a => a.status === 'interview').length}
                </p>
                <p className="text-sm text-gray-500">Interviews</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-5 border border-gray-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                <User className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">
                  {user?.license_type || 'N/A'}
                </p>
                <p className="text-sm text-gray-500">License</p>
              </div>
            </div>
          </div>
        </div>

        {folderCount > 0 && (
          <Link to="/my-referral-folders" className="block mb-8" data-testid="folder-reminder">
            <div className="bg-primary/5 border border-primary/15 rounded-xl p-4 flex items-center gap-3 hover:border-secondary transition-colors">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                <FolderOpen className="w-5 h-5 text-primary" />
              </div>
              <div className="flex-1">
                <p className="font-semibold text-primary">You have {folderCount} saved referral folder{folderCount === 1 ? '' : 's'}</p>
                <p className="text-sm text-gray-500">Open the Community Resource Directory to print or email referral lists to clients.</p>
              </div>
              <span className="text-secondary text-sm font-medium">Open →</span>
            </div>
          </Link>
        )}

        {/* Tabs */}
        <JobSeekerCtx.Provider value={{ activeTab, applications, applyParsedDataToProfile, deleteReason, deleteStep, deletingAccount, deletingApp, fetchData, handleArchiveApplication, handleDegreeUpload, handleDeleteAccount, handleDeleteDocument, handleLicenseUpload, handleParseResume, handleProfileSubmit, handleResumeUpload, handleUnsaveJob, handleWithdrawApplication, loading, logout, navigate, parsedResume, parsingResume, profileData, savedJobs, saving, setActiveTab, setApplications, setDeleteReason, setDeleteStep, setDeletingAccount, setDeletingApp, setLoading, setParsedResume, setParsingResume, setProfileData, setSavedJobs, setSaving, setShowDeleteConfirm, setShowParseResults, setUploading, setUploadingDegree, setUploadingLicense, showDeleteConfirm, showParseResults, updateProfile, uploadResume, uploading, uploadingDegree, uploadingLicense, user }}>
        <GettingStarted />

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-white border border-gray-100 p-1 mb-6">
            <TabsTrigger value="overview" className="data-[state=active]:bg-primary data-[state=active]:text-white">
              Overview
            </TabsTrigger>
            <TabsTrigger value="applications" className="data-[state=active]:bg-primary data-[state=active]:text-white">
              Applications
            </TabsTrigger>
            <TabsTrigger value="messages" className="data-[state=active]:bg-primary data-[state=active]:text-white">
              <MessageSquare className="w-4 h-4 mr-1" />
              Messages
            </TabsTrigger>
            <TabsTrigger value="saved" className="data-[state=active]:bg-primary data-[state=active]:text-white">
              Saved Jobs
            </TabsTrigger>
            <TabsTrigger value="alerts" className="data-[state=active]:bg-primary data-[state=active]:text-white" data-testid="tab-alerts">
              Saved Searches &amp; Alerts
            </TabsTrigger>
            <TabsTrigger value="matches" className="data-[state=active]:bg-primary data-[state=active]:text-white" data-testid="tab-matches">
              Job Matches
            </TabsTrigger>
            <TabsTrigger value="profile" className="data-[state=active]:bg-primary data-[state=active]:text-white">
              Profile
            </TabsTrigger>
            <TabsTrigger value="email-prefs" className="data-[state=active]:bg-primary data-[state=active]:text-white" data-testid="tab-email-prefs">
              Email Preferences
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <JobSeekerTabOverview />
          </TabsContent>

          {/* Applications Tab */}
          <TabsContent value="applications">
            <JobSeekerTabApplications />
          </TabsContent>

          {/* Messages Tab */}
          <TabsContent value="messages">
            <JobSeekerTabMessages />
          </TabsContent>

          {/* Saved Jobs Tab */}
          <TabsContent value="saved">
            <JobSeekerTabSaved />
          </TabsContent>

          {/* Saved Searches & Alerts Tab */}
          <TabsContent value="alerts">
            <JobSeekerTabAlerts />
          </TabsContent>

          {/* Job Matches Tab */}
          <TabsContent value="matches">
            <div className="bg-white rounded-xl border border-gray-100 p-8 text-center" data-testid="matches-tab-cta">
              <Sparkles className="w-10 h-10 text-secondary mx-auto mb-3" />
              <h3 className="font-serif text-xl font-bold text-primary">Your Job Preferences & Matches</h3>
              <p className="text-gray-500 mt-2 max-w-lg mx-auto">Tell us the social work role you want — degree, license, specialty, setting, location and salary — and we'll match you and email you when new matching jobs are posted.</p>
              <Button onClick={() => navigate('/job-preferences')} className="mt-5 bg-secondary hover:bg-secondary-hover text-white" data-testid="open-job-preferences-btn">
                Manage Preferences & See Matches <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </TabsContent>

          {/* Profile Tab */}
          <TabsContent value="profile">
            <JobSeekerTabProfile />
          </TabsContent>

          {/* Email Preferences Tab */}
          <TabsContent value="email-prefs">
            <JobSeekerTabEmailPrefs />
          </TabsContent>
        </Tabs>
        </JobSeekerCtx.Provider>
      </div>
    </div>
  );
};

export default JobSeekerDashboard;
